import { useState, useCallback, useMemo } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import {
  DateRange,
  CompareRange,
  MetricCardConfig,
  RevenueType,
} from "../types"
import { useCubeQuery, useLastUpdateDate } from "~/ui-rtk/hooks/cube"
import useDate from "~/ui-rtk/hooks/date"
import {
  MetricsMapping,
  type TMetric,
} from "~/ui-rtk/constants/metrics-mapping"
import {
  polyfillCubeQueryWithTimeDimension,
  resolveTimeDimensionKey,
} from "~/ui-rtk/utils/cube"
import { AggregationType } from "~/ui-rtk/constants/charts"
import useConnectedServices from "~/ui-rtk/hooks/connected-services"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"

const dayjs = useDate()

// Types for chart and table data
export type MarketingChartDataPoint = {
  date: string
  fullDate: string
  spend: number
  totalRevenue: number
  ncRevenue: number
  amazonRevenue: number
  mer: number
  aMer: number
}

export type CollectionTableRow = {
  collectionHandle: string
  collectionTitle: string
  collectionId: string
  collectionViewers: number
  addersToCart: number
  purchasers: number
  collectionToCartConversion: number
  collectionToCartAbandon: number
  cartToPurchaseConversion: number
  cartToPurchaseAbandon: number
  collectionToPurchaseConversion: number
  collectionToPurchaseAbandon: number
}

export type ProductTableRow = {
  productHandle: string
  productTitle: string
  productId: string
  productViewers: number
  addersToCart: number
  checkouts: number
  purchasers: number
  productToCartConversion: number
  productToCartAbandon: number
  cartToCheckoutConversion: number
  cartToCheckoutAbandon: number
  checkoutToPurchaseConversion: number
  checkoutToPurchaseAbandon: number
  productToPurchaseConversion: number
  productToPurchaseAbandon: number
}

type UseHomeDashboardReturn = {
  // Date state (now passed as parameters)
  revenueType: RevenueType
  isShopifyConnected: boolean
  // Date actions
  setRevenueType: (type: RevenueType) => void
  // Metrics state
  metricCards: MetricCardConfig[]
  isLoading: boolean
  // Chart data
  marketingChartData: MarketingChartDataPoint[]
  compareMarketingChartData: MarketingChartDataPoint[]
  isMarketingChartLoading: boolean
  // Table data
  topCollections: CollectionTableRow[]
  isCollectionsLoading: boolean
  topProducts: ProductTableRow[]
  isProductsLoading: boolean
  hasMerchandisingData: boolean
  // Waterfall chart data
  waterfallData: {
    totalRevenue: number | null
    orderCosts: number | null
    adSpend: number | null
    contributionMargin: number | null
  }
  isAmazonConnected: boolean
}

// Map AggregationType to Cube.js granularity
const granularityMap: Record<AggregationType, "day" | "week" | "month"> = {
  [AggregationType.DAILY]: "day",
  [AggregationType.WEEKLY]: "week",
  [AggregationType.MONTHLY]: "month",
}

// Get metric keys based on revenue type
const getMetricKeys = (revenueType: RevenueType) => {
  switch (revenueType) {
    case "total":
      return {
        newCustomerRevenue: "metrics.shopify_total_new_customer_revenue",
        repeatCustomerRevenue: "metrics.shopify_total_repeat_customer_revenue",
        totalRevenue: "metrics.shopify_total_customer_revenue",
      }
    case "ecomm":
      return {
        newCustomerRevenue: "metrics.shopify_ecomm_new_customer_revenue",
        repeatCustomerRevenue: "metrics.shopify_ecomm_repeat_customer_revenue",
        totalRevenue: "metrics.shopify_ecomm_total_customer_revenue",
      }
    case "retail":
      return {
        newCustomerRevenue: "metrics.shopify_dtc_retail_new_customer_revenue",
        repeatCustomerRevenue:
          "metrics.shopify_dtc_retail_repeat_customer_revenue",
        totalRevenue: "metrics.shopify_dtc_retail_total_customer_revenue",
      }
  }
}

// Build query with date range from MetricsMapping
const buildQueryWithDateRange = (
  queryKey: keyof typeof MetricsMapping,
  dateRange: DateRange,
  granularity?: AggregationType,
): TCubeQuery => {
  const baseQuery = MetricsMapping[queryKey].q as TMetric
  return polyfillCubeQueryWithTimeDimension(baseQuery, {
    dateRange: [dateRange.start, dateRange.end],
    granularity: granularity ? granularityMap[granularity] : undefined,
  })
}

// Calculate trend between current and previous values
const calculateTrend = (
  current: number | null,
  previous: number | null,
): { trend: "up" | "down" | "neutral"; trendValue: number } | undefined => {
  // Guard against null, undefined, zero, NaN, or Infinity
  if (
    current === null ||
    previous === null ||
    previous === 0 ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous)
  ) {
    return undefined
  }

  const change = ((current - previous) / previous) * 100

  // Guard against NaN/Infinity results and cap at reasonable bounds
  if (!Number.isFinite(change)) return undefined
  if (Math.abs(change) > 10000) return undefined // Cap at 10000%

  // Determine trend direction
  let trend: "up" | "down" | "neutral" = "neutral"
  if (change > 0.01) {
    trend = "up"
  } else if (change < -0.01) {
    trend = "down"
  }

  return {
    trend,
    trendValue: change,
  }
}

export default function useHomeDashboard(
  dateRange: DateRange,
  compareRange: CompareRange | null,
  granularity: AggregationType,
): UseHomeDashboardReturn {
  // Get last update date for loading state
  const { isLoading: isLastDateLoading } = useLastUpdateDate()

  const [revenueType, setRevenueType] = useState<RevenueType>("ecomm")

  const compareEnabled = !!compareRange

  // Build queries using MetricsMapping
  const mainQuery = useMemo(
    () => buildQueryWithDateRange("home_dashboard_metrics", dateRange),
    [dateRange],
  )
  const compQuery = useMemo(
    () =>
      compareEnabled && compareRange
        ? buildQueryWithDateRange("home_dashboard_metrics", compareRange)
        : null,
    [compareEnabled, compareRange],
  )

  // Build chart/table queries using MetricsMapping
  const dailyChartQuery = useMemo(
    () =>
      buildQueryWithDateRange(
        "home_dashboard_daily_chart",
        dateRange,
        granularity,
      ),
    [dateRange, granularity],
  )
  const compareDailyChartQuery = useMemo(
    () =>
      compareEnabled && compareRange
        ? buildQueryWithDateRange(
            "home_dashboard_daily_chart",
            compareRange,
            granularity,
          )
        : null,
    [compareEnabled, compareRange, granularity],
  )
  const collectionsQuery = useMemo(
    () => buildQueryWithDateRange("home_dashboard_top_collections", dateRange),
    [dateRange],
  )
  const productsQuery = useMemo(
    () => buildQueryWithDateRange("home_dashboard_top_products", dateRange),
    [dateRange],
  )

  // Fetch main data
  const { resultSet: mainResult, isLoading: mainLoading } =
    useCubeQuery(mainQuery)

  // Fetch comparison data (skip if not enabled)
  const { resultSet: compResult, isLoading: compLoading } = useCubeQuery(
    compQuery ?? { measures: [] },
    { skip: !compareEnabled || !compQuery },
  )

  // Fetch daily chart data
  const { resultSet: dailyChartResult, isLoading: isMarketingChartLoading } =
    useCubeQuery(dailyChartQuery)
  const {
    resultSet: compareDailyChartResult,
    isLoading: isCompareChartLoading,
  } = useCubeQuery(compareDailyChartQuery ?? { measures: [] }, {
    skip: !compareEnabled || !compareDailyChartQuery,
  })

  // Fetch collections data
  const { resultSet: collectionsResult, isLoading: isCollectionsLoading } =
    useCubeQuery(collectionsQuery)

  // Fetch products data
  const { resultSet: productsResult, isLoading: isProductsLoading } =
    useCubeQuery(productsQuery)

  const isLoading =
    isLastDateLoading || mainLoading || (compareEnabled && compLoading)

  // Check if Shopify and Amazon Ads are connected via company connectors.
  // Uses group-aware lookup so visibility survives Fivetran → Airbyte migration
  // (a company on shopify_airbyte still counts as "Shopify connected").
  const { findCompanyConnectorInGroup } = useConnectedServices()
  const shopifyConnector = findCompanyConnectorInGroup(CONNECTORS_IDS.SHOPIFY)
  const amazonAdsConnector = findCompanyConnectorInGroup(
    CONNECTORS_IDS.AMAZON_ADS,
  )
  // Use isCompleted to check if connectors are properly set up (same pattern as sources.ts)
  const isShopifyConnected = shopifyConnector?.isCompleted ?? false
  const isAmazonConnected = amazonAdsConnector?.isCompleted ?? false
  // Extract values from result sets
  const extractValue = useCallback(
    (resultSet: typeof mainResult, measureKey: string): number | null => {
      if (!resultSet) return null
      try {
        const data = resultSet.tablePivot()
        if (data.length > 0) {
          const value = data[0][measureKey]
          return typeof value === "number"
            ? value
            : parseFloat(value as string) || null
        }
      } catch {
        return null
      }
      return null
    },
    [],
  )

  // Process metrics data
  const metricCards = useMemo<MetricCardConfig[]>(() => {
    // Get metric keys based on revenue type
    const metricKeys = getMetricKeys(revenueType)

    // Customer Acquisition metrics
    const ncRevenue = extractValue(mainResult, metricKeys.newCustomerRevenue)
    const totalSpend = extractValue(mainResult, "metrics.total_ad_spend")
    const aMer = extractValue(mainResult, "metrics.amer")

    // Previous period values for trends
    const prevNcRevenue = extractValue(
      compResult,
      metricKeys.newCustomerRevenue,
    )
    const prevTotalSpend = extractValue(compResult, "metrics.total_ad_spend")
    const prevAMer = extractValue(compResult, "metrics.amer")

    // Retention metrics
    const repeatRevenue = extractValue(
      mainResult,
      metricKeys.repeatCustomerRevenue,
    )
    const prevRepeatRevenue = extractValue(
      compResult,
      metricKeys.repeatCustomerRevenue,
    )

    // Brand metrics
    const brandValue = extractValue(mainResult, "metrics.d180_brand_value")
    const brandedSearches = extractValue(
      mainResult,
      "metrics.google_search_organic_branded_search_impressions",
    )
    const gaBaselineRevenue = extractValue(
      mainResult,
      "metrics.ga_baseline_revenue",
    )
    const gaTotalRevenue = extractValue(mainResult, "metrics.ga_total_revenue")
    const baselineRevenuePercent =
      gaTotalRevenue && gaBaselineRevenue
        ? gaBaselineRevenue / gaTotalRevenue
        : null

    const prevBrandValue = extractValue(compResult, "metrics.d180_brand_value")
    const prevBrandedSearches = extractValue(
      compResult,
      "metrics.google_search_organic_branded_search_impressions",
    )
    const prevGaBaselineRevenue = extractValue(
      compResult,
      "metrics.ga_baseline_revenue",
    )
    const prevGaTotalRevenue = extractValue(
      compResult,
      "metrics.ga_total_revenue",
    )
    const prevBaselineRevenuePercent =
      prevGaTotalRevenue && prevGaBaselineRevenue
        ? prevGaBaselineRevenue / prevGaTotalRevenue
        : null

    // Marketing Efficiency metrics - use Shopify revenue or GA revenue fallback
    const shopifyTotalRevenue = extractValue(
      mainResult,
      metricKeys.totalRevenue,
    )
    const gaRevenue = extractValue(mainResult, "metrics.ga_total_revenue")
    const revenueForMer = isShopifyConnected ? shopifyTotalRevenue : gaRevenue

    const mer =
      revenueForMer && totalSpend && totalSpend > 0
        ? revenueForMer / totalSpend
        : null
    const paidRevenue = extractValue(mainResult, "metrics.shopify_paid_revenue")
    const contributionMargin = extractValue(
      mainResult,
      "metrics.total_contribution_margin",
    )

    const prevShopifyTotalRevenue = extractValue(
      compResult,
      metricKeys.totalRevenue,
    )
    const prevGaRevenue = extractValue(compResult, "metrics.ga_total_revenue")
    const prevRevenueForMer = isShopifyConnected
      ? prevShopifyTotalRevenue
      : prevGaRevenue

    const prevMer =
      prevRevenueForMer && prevTotalSpend && prevTotalSpend > 0
        ? prevRevenueForMer / prevTotalSpend
        : null
    const prevPaidRevenue = extractValue(
      compResult,
      "metrics.shopify_paid_revenue",
    )
    const prevContributionMargin = extractValue(
      compResult,
      "metrics.total_contribution_margin",
    )

    // Consideration metrics
    const sessions = extractValue(mainResult, "metrics.ga_sessions")
    const baselineSessions = extractValue(
      mainResult,
      "metrics.ga_baseline_sessions",
    )
    const engagedSessions = extractValue(
      mainResult,
      "metrics.ga_engaged_sessions_from_new_visitors",
    )

    const prevSessions = extractValue(compResult, "metrics.ga_sessions")
    const prevBaselineSessions = extractValue(
      compResult,
      "metrics.ga_baseline_sessions",
    )
    const prevEngagedSessions = extractValue(
      compResult,
      "metrics.ga_engaged_sessions_from_new_visitors",
    )

    // Conversion metrics
    const revenuePerSession = extractValue(
      mainResult,
      "metrics.total_per_session_value",
    )
    const baselineRevenuePerSession = extractValue(
      mainResult,
      "metrics.baseline_per_session_value",
    )

    const prevRevenuePerSession = extractValue(
      compResult,
      "metrics.total_per_session_value",
    )
    const prevBaselineRevenuePerSession = extractValue(
      compResult,
      "metrics.baseline_per_session_value",
    )

    return [
      {
        id: "customerAcquisition",
        title: "Customer Acquisition",
        metrics: isShopifyConnected
          ? [
              {
                label: "Revenue from New Customers",
                value: ncRevenue,
                format: "currency",
                ...calculateTrend(ncRevenue, prevNcRevenue),
              },
              {
                label: "aMER",
                value: aMer,
                format: "decimal",
                ...calculateTrend(aMer, prevAMer),
              },
            ]
          : [
              {
                label: "Connect Shopify",
                value: null,
                format: "number",
              },
            ],
      },
      {
        id: "retention",
        title: "Retention",
        metrics: isShopifyConnected
          ? [
              {
                label: "Revenue from Repeat Customers",
                value: repeatRevenue,
                format: "currency",
                ...calculateTrend(repeatRevenue, prevRepeatRevenue),
              },
            ]
          : [
              {
                label: "Connect Shopify",
                value: null,
                format: "number",
              },
            ],
      },
      {
        id: "brand",
        title: "Brand",
        metrics: [
          {
            label: "Brand Value",
            value: brandValue,
            format: "currency",
            ...calculateTrend(brandValue, prevBrandValue),
          },
          {
            label: "Branded Searches",
            value: brandedSearches,
            format: "number",
            ...calculateTrend(brandedSearches, prevBrandedSearches),
          },
          {
            label: "GA Baseline Revenue (% of Total)",
            value: baselineRevenuePercent,
            format: "percent",
            ...calculateTrend(
              baselineRevenuePercent,
              prevBaselineRevenuePercent,
            ),
          },
        ],
      },
      {
        id: "marketingEfficiency",
        title: "Marketing Efficiency",
        metrics: isShopifyConnected
          ? [
              {
                label: "MER",
                value: mer,
                format: "decimal",
                ...calculateTrend(mer, prevMer),
              },
              {
                label: "aMER",
                value: aMer,
                format: "decimal",
                ...calculateTrend(aMer, prevAMer),
              },
              {
                label: "Total Spend",
                value: totalSpend,
                format: "currency",
                ...calculateTrend(totalSpend, prevTotalSpend),
              },
              {
                label: "Total Modeled Paid Revs",
                value: paidRevenue,
                format: "currency",
                ...calculateTrend(paidRevenue, prevPaidRevenue),
              },
              {
                // metrics.total_contribution_margin is ecomm-scoped (DTC
                // E-Commerce + Subscription) despite the legacy key name.
                label: "Ecomm Contribution Margin",
                value: contributionMargin,
                format: "currency",
                ...calculateTrend(contributionMargin, prevContributionMargin),
              },
            ]
          : [
              {
                label: "MER",
                value: mer,
                format: "decimal",
                ...calculateTrend(mer, prevMer),
              },
              {
                label: "Total Spend",
                value: totalSpend,
                format: "currency",
                ...calculateTrend(totalSpend, prevTotalSpend),
              },
            ],
      },
      {
        id: "consideration",
        title: "Consideration",
        metrics: [
          {
            label: "Sessions",
            value: sessions,
            format: "number",
            ...calculateTrend(sessions, prevSessions),
          },
          {
            label: "Engaged Sessions",
            value: engagedSessions,
            format: "number",
            ...calculateTrend(engagedSessions, prevEngagedSessions),
          },
          {
            label: "Baseline Sessions",
            value: baselineSessions,
            format: "number",
            ...calculateTrend(baselineSessions, prevBaselineSessions),
          },
        ],
      },
      {
        id: "conversion",
        title: "Conversion",
        metrics: [
          {
            label: "Revenue Per Session",
            value: revenuePerSession,
            format: "currency",
            ...calculateTrend(revenuePerSession, prevRevenuePerSession),
          },
          {
            label: "Baseline Revenue per Session",
            value: baselineRevenuePerSession,
            format: "currency",
            ...calculateTrend(
              baselineRevenuePerSession,
              prevBaselineRevenuePerSession,
            ),
          },
        ],
      },
    ]
  }, [mainResult, compResult, extractValue, revenueType, isShopifyConnected])

  // Waterfall chart data - Revenue to Profit breakdown
  // Uses shopify_total_order_cost for order costs
  // For ad spend: use total_ad_spend if Amazon connected, otherwise subtract amazon_ads_total_spend
  const waterfallData = useMemo(() => {
    const totalRevenue = extractValue(
      mainResult,
      "metrics.shopify_total_customer_revenue",
    )
    const orderCosts = extractValue(
      mainResult,
      "metrics.shopify_total_order_cost",
    )
    const totalAdSpend = extractValue(mainResult, "metrics.total_ad_spend")
    const amazonAdSpend = extractValue(
      mainResult,
      "metrics.amazon_ads_total_spend",
    )
    const contributionMargin = extractValue(
      mainResult,
      "metrics.total_contribution_margin",
    )

    // Calculate ad spend for waterfall chart:
    // - total_ad_spend includes spend from all platforms (Meta, Google, TikTok, Amazon, etc.)
    // - If Amazon Ads connector is connected, show the full total_ad_spend
    // - If Amazon Ads is NOT connected, subtract amazon_ads_total_spend to avoid showing
    //   Amazon spend that might be included via other data sources but isn't actively tracked
    let adSpend = totalAdSpend
    if (!isAmazonConnected && totalAdSpend !== null && amazonAdSpend !== null) {
      adSpend = totalAdSpend - amazonAdSpend
    }

    return {
      totalRevenue,
      orderCosts,
      adSpend,
      contributionMargin,
    }
  }, [mainResult, extractValue, isAmazonConnected])

  // Process daily chart data for marketing trends
  const marketingChartData = useMemo<MarketingChartDataPoint[]>(() => {
    if (!dailyChartResult) return []

    try {
      const data = dailyChartResult.tablePivot()

      // Map granularity to the Cube.js date key and display format
      const dateKeyMap: Record<AggregationType, string> = {
        [AggregationType.DAILY]: "metrics.date.day",
        [AggregationType.WEEKLY]: "metrics.date.week",
        [AggregationType.MONTHLY]: "metrics.date.month",
      }

      const dateFormatMap: Record<AggregationType, string> = {
        [AggregationType.DAILY]: "MMM D",
        [AggregationType.WEEKLY]: "MMM D",
        [AggregationType.MONTHLY]: "MMM YYYY",
      }

      // Weekly keys may come back rewritten to the company's custom week
      // granularity (e.g. `metrics.date.week_sunday`), so resolve from the rows.
      const dateKey = resolveTimeDimensionKey(
        data,
        "metrics.date",
        dateKeyMap[granularity],
      )
      const dateFormat = dateFormatMap[granularity]

      // Get metric keys based on revenue type
      const metricKeys = getMetricKeys(revenueType)

      return data.map(row => {
        // Get date based on current granularity
        const dateStr =
          (row[dateKey] as string) || (row["metrics.date"] as string) || ""
        const spend = Number(row["metrics.total_ad_spend"]) || 0

        // Use dynamic revenue metrics based on selected type
        const totalRevenue = isShopifyConnected
          ? Number(row[metricKeys.totalRevenue]) || 0
          : Number(row["metrics.ga_total_revenue"]) || 0
        const ncRevenue = isShopifyConnected
          ? Number(row[metricKeys.newCustomerRevenue]) || 0
          : 0
        const amazonRevenue = isAmazonConnected
          ? Number(row["metrics.amazon_total_revenue"]) || 0
          : 0
        const aMer = Number(row["metrics.amer"]) || 0
        const mer = spend > 0 ? totalRevenue / spend : 0

        return {
          date: dateStr ? dayjs(dateStr).format(dateFormat) : "Unknown",
          fullDate: dateStr,
          spend,
          totalRevenue,
          ncRevenue,
          amazonRevenue,
          mer: Number(mer.toFixed(2)),
          aMer: Number(aMer.toFixed(2)),
        }
      })
    } catch {
      return []
    }
  }, [
    dailyChartResult,
    granularity,
    revenueType,
    isShopifyConnected,
    isAmazonConnected,
  ])

  // Process comparison chart data for marketing trends
  const compareMarketingChartData = useMemo<MarketingChartDataPoint[]>(() => {
    if (!compareDailyChartResult || !compareEnabled) return []

    try {
      const data = compareDailyChartResult.tablePivot()

      // Map granularity to the Cube.js date key and display format
      const dateKeyMap: Record<AggregationType, string> = {
        [AggregationType.DAILY]: "metrics.date.day",
        [AggregationType.WEEKLY]: "metrics.date.week",
        [AggregationType.MONTHLY]: "metrics.date.month",
      }

      const dateFormatMap: Record<AggregationType, string> = {
        [AggregationType.DAILY]: "MMM D",
        [AggregationType.WEEKLY]: "MMM D",
        [AggregationType.MONTHLY]: "MMM YYYY",
      }

      // Weekly keys may come back rewritten to the company's custom week
      // granularity (e.g. `metrics.date.week_sunday`), so resolve from the rows.
      const dateKey = resolveTimeDimensionKey(
        data,
        "metrics.date",
        dateKeyMap[granularity],
      )
      const dateFormat = dateFormatMap[granularity]

      // Get metric keys based on revenue type
      const metricKeys = getMetricKeys(revenueType)

      return data.map(row => {
        const dateStr =
          (row[dateKey] as string) || (row["metrics.date"] as string) || ""
        const spend = Number(row["metrics.total_ad_spend"]) || 0

        // Use dynamic revenue metrics based on selected type
        const totalRevenue = isShopifyConnected
          ? Number(row[metricKeys.totalRevenue]) || 0
          : Number(row["metrics.ga_total_revenue"]) || 0
        const ncRevenue = isShopifyConnected
          ? Number(row[metricKeys.newCustomerRevenue]) || 0
          : 0
        const amazonRevenue = isAmazonConnected
          ? Number(row["metrics.amazon_total_revenue"]) || 0
          : 0
        const aMer = Number(row["metrics.amer"]) || 0
        const mer = spend > 0 ? totalRevenue / spend : 0

        return {
          date: dateStr ? dayjs(dateStr).format(dateFormat) : "Unknown",
          fullDate: dateStr,
          spend,
          totalRevenue,
          ncRevenue,
          amazonRevenue,
          mer: Number(mer.toFixed(2)),
          aMer: Number(aMer.toFixed(2)),
        }
      })
    } catch {
      return []
    }
  }, [
    compareDailyChartResult,
    compareEnabled,
    granularity,
    revenueType,
    isShopifyConnected,
    isAmazonConnected,
  ])

  // Process collections data
  const topCollections = useMemo<CollectionTableRow[]>(() => {
    if (!collectionsResult) return []

    try {
      const data = collectionsResult.tablePivot()
      const CUBE = "company_web_analytics_collection_funnel"

      return data.map(row => {
        const collectionViewers = Number(row[`${CUBE}.collection_viewers`]) || 0
        const addersToCart = Number(row[`${CUBE}.adders_to_cart`]) || 0
        const purchasers = Number(row[`${CUBE}.purchasers`]) || 0

        // Calculate conversion rates on frontend
        const collectionToCartConversion =
          collectionViewers > 0 ? (addersToCart / collectionViewers) * 100 : 0
        const cartToPurchaseConversion =
          addersToCart > 0 ? (purchasers / addersToCart) * 100 : 0
        const collectionToPurchaseConversion =
          collectionViewers > 0 ? (purchasers / collectionViewers) * 100 : 0

        // Calculate abandon rates
        const collectionToCartAbandon = 100 - collectionToCartConversion
        const cartToPurchaseAbandon = 100 - cartToPurchaseConversion
        const collectionToPurchaseAbandon = 100 - collectionToPurchaseConversion

        return {
          collectionHandle: (row[`${CUBE}.collection_handle`] as string) || "",
          collectionTitle:
            (row[`${CUBE}.collection_title`] as string) || "Unnamed Collection",
          collectionId: (row[`${CUBE}.collection_id`] as string) || "",
          collectionViewers,
          addersToCart,
          purchasers,
          collectionToCartConversion,
          collectionToCartAbandon,
          cartToPurchaseConversion,
          cartToPurchaseAbandon,
          collectionToPurchaseConversion,
          collectionToPurchaseAbandon,
        }
      })
    } catch {
      return []
    }
  }, [collectionsResult])

  // Process products data
  const topProducts = useMemo<ProductTableRow[]>(() => {
    if (!productsResult) return []

    try {
      const data = productsResult.tablePivot()
      const CUBE = "company_web_analytics_product_page_funnel"

      return data.map(row => {
        const productViewers = Number(row[`${CUBE}.product_viewers`]) || 0
        const addersToCart = Number(row[`${CUBE}.adders_to_cart`]) || 0
        const checkouts = Number(row[`${CUBE}.checkouts`]) || 0
        const purchasers = Number(row[`${CUBE}.purchasers`]) || 0

        // Calculate conversion rates on frontend (to avoid GROUP BY issues)
        const productToCartConversion =
          productViewers > 0 ? (addersToCart / productViewers) * 100 : 0
        const cartToCheckoutConversion =
          addersToCart > 0 ? (checkouts / addersToCart) * 100 : 0
        const checkoutToPurchaseConversion =
          checkouts > 0 ? (purchasers / checkouts) * 100 : 0
        const productToPurchaseConversion =
          productViewers > 0 ? (purchasers / productViewers) * 100 : 0

        // Calculate abandon rates
        const productToCartAbandon = 100 - productToCartConversion
        const cartToCheckoutAbandon = 100 - cartToCheckoutConversion
        const checkoutToPurchaseAbandon = 100 - checkoutToPurchaseConversion
        const productToPurchaseAbandon = 100 - productToPurchaseConversion

        return {
          productHandle: (row[`${CUBE}.product_handle`] as string) || "",
          productTitle:
            (row[`${CUBE}.product_title`] as string) || "Unnamed Product",
          productId: (row[`${CUBE}.product_id`] as string) || "",
          productViewers,
          addersToCart,
          checkouts,
          purchasers,
          productToCartConversion,
          productToCartAbandon,
          cartToCheckoutConversion,
          cartToCheckoutAbandon,
          checkoutToPurchaseConversion,
          checkoutToPurchaseAbandon,
          productToPurchaseConversion,
          productToPurchaseAbandon,
        }
      })
    } catch {
      return []
    }
  }, [productsResult])

  return {
    // Date state (now passed as parameters)
    revenueType,
    isShopifyConnected,
    // Date actions
    setRevenueType,
    // Metrics state
    metricCards,
    isLoading,
    // Chart data
    marketingChartData,
    compareMarketingChartData,
    isMarketingChartLoading: isMarketingChartLoading || isCompareChartLoading,
    // Table data
    topCollections,
    isCollectionsLoading,
    topProducts,
    isProductsLoading,
    // Check if merchandising data is available (either has data or is still loading)
    hasMerchandisingData:
      topCollections.length > 0 ||
      topProducts.length > 0 ||
      isCollectionsLoading ||
      isProductsLoading,
    // Waterfall chart data
    waterfallData,
    isAmazonConnected,
  }
}
